import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Stack,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Send as SendIcon } from '@mui/icons-material';
import adminService from '../../services/adminService';

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 16,
  border: `1px solid ${theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.08)'}`,
  background: theme.palette.mode === 'light' 
    ? 'rgba(255, 255, 255, 0.8)'
    : 'rgba(15, 23, 42, 0.8)',
  backdropFilter: 'blur(10px)',
}));

const AdminNotificationsPage: React.FC = () => {
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [type, setType] = useState<'announcement' | 'alert' | 'update'>('announcement');
  const [priority, setPriority] = useState<'low' | 'medium' | 'high' | 'urgent'>('medium');
  const [targetUsers, setTargetUsers] = useState<'all' | 'admins'>('all');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [openPreview, setOpenPreview] = useState(false);

  const handleSend = async () => {
    if (!title.trim() || !message.trim()) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // Get current user ID from auth
      const user = JSON.parse(localStorage.getItem('currentUser') || '{}');
      if (!user.uid) {
        setError('User authentication failed');
        return;
      }

      await adminService.createSystemNotification(
        user.uid,
        title,
        message,
        type,
        targetUsers === 'all' ? 'all' : ['admin'],
        priority
      );

      setSuccess('Notification sent successfully!');
      setTitle('');
      setMessage('');
      setType('announcement');
      setPriority('medium');
      
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error sending notification:', err);
      setError('Failed to send notification');
    } finally {
      setLoading(false);
    }
  };

  const getPriorityColor = (priority: string): 'error' | 'warning' | 'info' | 'success' => {
    switch (priority) {
      case 'urgent': return 'error';
      case 'high': return 'warning';
      case 'medium': return 'info';
      default: return 'success';
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 700 }}>
        System Notifications
      </Typography>

      <StyledCard>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 3, fontWeight: 700 }}>
            Broadcast Message to Users
          </Typography>

          {success && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
              {success}
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Title */}
            <TextField
              fullWidth
              label="Notification Title"
              placeholder="e.g., Scheduled Maintenance"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              disabled={loading}
              size="small"
            />

            {/* Message */}
            <TextField
              fullWidth
              label="Message"
              placeholder="Enter the notification message..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              disabled={loading}
              multiline
              rows={4}
              size="small"
            />

            {/* Type */}
            <FormControl size="small" fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                value={type}
                label="Type"
                onChange={(e) => setType(e.target.value as 'announcement' | 'alert' | 'update')}
                disabled={loading}
              >
                <MenuItem value="announcement">Announcement</MenuItem>
                <MenuItem value="alert">Alert</MenuItem>
                <MenuItem value="update">Update</MenuItem>
              </Select>
            </FormControl>

            {/* Priority */}
            <FormControl size="small" fullWidth>
              <InputLabel>Priority</InputLabel>
              <Select
                value={priority}
                label="Priority"
                onChange={(e) => setPriority(e.target.value as 'low' | 'medium' | 'high' | 'urgent')}
                disabled={loading}
              >
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="urgent">Urgent</MenuItem>
              </Select>
            </FormControl>

            {/* Target Users */}
            <FormControl size="small" fullWidth>
              <InputLabel>Send To</InputLabel>
              <Select
                value={targetUsers}
                label="Send To"
                onChange={(e) => setTargetUsers(e.target.value as 'all' | 'admins')}
                disabled={loading}
              >
                <MenuItem value="all">All Users</MenuItem>
                <MenuItem value="admins">Admins Only</MenuItem>
              </Select>
            </FormControl>

            {/* Preview */}
            <Box sx={{ bgcolor: 'rgba(79, 209, 199, 0.05)', p: 2, borderRadius: 1 }}>
              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1 }}>
                Preview
              </Typography>
              <Stack spacing={1}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip 
                    label={type.toUpperCase()} 
                    size="small" 
                    variant="outlined"
                  />
                  <Chip 
                    label={priority.toUpperCase()} 
                    size="small"
                    color={getPriorityColor(priority)}
                    variant="outlined"
                  />
                </Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {title || 'Notification Title'}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {message || 'Notification message will appear here...'}
                </Typography>
              </Stack>
            </Box>

            {/* Actions */}
            <Box sx={{ display: 'flex', gap: 2, pt: 2 }}>
              <Button
                variant="contained"
                startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
                onClick={handleSend}
                disabled={loading}
                sx={{ 
                  background: 'linear-gradient(135deg, #38B2AC 0%, #4FD1C7 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #2d9d96 0%, #3fb5b0 100%)',
                  },
                }}
              >
                {loading ? 'Sending...' : 'Send Notification'}
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  setTitle('');
                  setMessage('');
                  setType('announcement');
                  setPriority('medium');
                  setError(null);
                  setSuccess(null);
                }}
                disabled={loading}
              >
                Clear
              </Button>
            </Box>
          </Box>
        </CardContent>
      </StyledCard>

      {/* Info Box */}
      <Box sx={{ mt: 3, p: 2, bgcolor: 'rgba(79, 209, 199, 0.05)', borderRadius: 2 }}>
        <Typography variant="body2" color="textSecondary">
          💡 <strong>Tip:</strong> System notifications are sent in real-time to all connected users. Use high priority for critical updates.
        </Typography>
      </Box>
    </Box>
  );
};

export default AdminNotificationsPage;
